import { useState } from 'react';
import { Checkbox } from '../../../components/ui/Checkbox';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import { QuoteLine, Product } from '../types';

interface LicensesSectionProps {
  included: boolean;
  lines: QuoteLine[];
  products: Product[];
  onToggleIncluded: (included: boolean) => void;
  onUpdateLine: (lineId: string, updates: Partial<QuoteLine>) => void;
  onAddLine: () => void;
  onRemoveLine: (lineId: string) => void;
  monthlySubtotal: number;
  annualSubtotal: number;
}

const LicensesSection = ({
  included,
  lines,
  products,
  onToggleIncluded,
  onUpdateLine,
  onAddLine,
  onRemoveLine,
  monthlySubtotal,
  annualSubtotal,
}: LicensesSectionProps) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const getProductOptions = () => {
    // Sort products by group name first, then by product name
    // Add null/undefined checks to prevent localeCompare errors
    const sortedProducts = [...products].sort((a, b) => {
      const groupA = a?.groupName || '';
      const groupB = b?.groupName || '';
      
      if (groupA !== groupB) {
        return groupA.localeCompare(groupB);
      }
      
      const productA = a?.productName || '';
      const productB = b?.productName || '';
      return productA.localeCompare(productB);
    });

    return sortedProducts.map((p) => ({
      value: p?.id || '',
      label: `${p?.groupName || 'Unknown'} - ${p?.productName || 'Unknown'}`,
    }));
  };

  const handleProductChange = (lineId: string, productId: string) => {
    const product = products.find((p) => p?.id === productId);
    if (product) {
      onUpdateLine(lineId, {
        productId: product.id,
        productName: product.productName,
        groupName: product.groupName,
        unitPrice: product.unitPrice,
      });
    }
  };

  const calculateLineAmounts = (line: QuoteLine) => {
    // Safe calculation with proper null/undefined handling
    const unitPrice = Number(line.unitPrice) || 0;
    const quantity = Number(line.quantity) || 0;
    const monthly = unitPrice * quantity;
    const annual = monthly * 12;
    return { monthly, annual };
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-sm">
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1 hover:bg-muted rounded transition-colors duration-150"
            aria-label={isExpanded ? 'Collapse section' : 'Expand section'}
          >
            <Icon
              name={isExpanded ? 'ChevronDown' : 'ChevronRight'}
              size={20}
              className="text-text-secondary"
            />
          </button>
          <Icon name="Package" size={20} className="text-primary" />
          <h2 className="text-lg font-semibold text-text-primary">Licenses</h2>
        </div>
        <Checkbox
          label="Include in Quote"
          checked={included}
          onChange={(e) => onToggleIncluded(e.target.checked)}
        />
      </div>

      {isExpanded && included && (
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-end">
            <Button
              variant="outline"
              size="sm"
              iconName="Plus"
              iconPosition="left"
              onClick={onAddLine}
            >
              Add License
            </Button>
          </div>

          <div className="overflow-visible">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-muted">
                  <th className="px-4 py-3 text-left text-sm font-semibold text-text-primary border-b border-border w-1/4">
                    Group
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-text-primary border-b border-border w-1/4">
                    Product
                  </th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-text-primary border-b border-border">
                    Unit Price (€/month)
                  </th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-text-primary border-b border-border">
                    Quantity
                  </th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-text-primary border-b border-border">
                    Monthly (€)
                  </th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-text-primary border-b border-border">
                    Annual (€)
                  </th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-text-primary border-b border-border">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {lines.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-text-secondary">
                      <Icon name="Package" size={48} className="mx-auto mb-2 opacity-30" />
                      <p>No licenses added yet. Click "Add License" to get started.</p>
                    </td>
                  </tr>
                ) : (
                  lines.map((line) => {
                    const amounts = calculateLineAmounts(line);
                    return (
                      <tr key={line.id} className="border-b border-border hover:bg-muted/50">
                        <td className="px-4 py-3">
                          <Input
                            type="text"
                            value={line.group || ''}
                            onChange={(e) => onUpdateLine(line.id, { group: e.target.value })}
                            placeholder="Group name"
                            className="text-sm"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <Select
                            options={getProductOptions()}
                            value={line.productId}
                            onChange={(value) => handleProductChange(line.id, value as string)}
                            placeholder="Select product"
                            className="min-w-[200px] w-full"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <Input
                            type="number"
                            value={line.unitPrice || 0}
                            onChange={(e) =>
                              onUpdateLine(line.id, { unitPrice: parseFloat(e.target.value) || 0 })
                            }
                            className="text-right font-mono"
                            min="0"
                            step="0.01"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <Input
                            type="number"
                            value={line.quantity || 0}
                            onChange={(e) =>
                              onUpdateLine(line.id, { quantity: parseInt(e.target.value) || 0 })
                            }
                            className="text-right"
                            min="1"
                          />
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-sm text-text-primary">
                          €{amounts.monthly.toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-sm text-text-primary">
                          €{amounts.annual.toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Button
                            variant="ghost"
                            size="icon"
                            iconName="Trash2"
                            onClick={() => onRemoveLine(line.id)}
                            className="text-error hover:text-error hover:bg-error/10"
                            aria-label="Remove line"
                          />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
              {lines.length > 0 && (
                <tfoot>
                  <tr className="bg-muted font-semibold">
                    <td colSpan={3} className="px-4 py-3 text-right text-sm text-text-primary">
                      Subtotal:
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-sm text-primary">
                      €{monthlySubtotal.toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-sm text-primary">
                      €{annualSubtotal.toFixed(2)}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}

      {isExpanded && !included && (
        <div className="p-6 text-center text-text-secondary">
          <Icon name="PackageX" size={48} className="mx-auto mb-2 opacity-30" />
          <p>Licenses section excluded from quote</p>
        </div>
      )}
    </div>
  );
};

export default LicensesSection;